import { supabase } from './supabaseClient';
import { ensureAuthenticated } from './auth';
import { logAuthDebug } from './authDebugLogger';
// NOTE: Coin/powerup defaults are now handled by Postgres column defaults
// and the handle_new_user() auth trigger. No client-side defaults needed.

// Get user's coin balance
export const getCoins = async (userOverride = null) => {
  const user = userOverride || (await ensureAuthenticated()).user;
  logAuthDebug('coins_get_start', {
    hasUserOverride: Boolean(userOverride?.id),
    userId: user?.id || null,
  });
  if (!user) return 0;
  
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('coins')
      .eq('id', user.id)
      .maybeSingle();
    
    if (error) {
      logAuthDebug('coins_get_error', {
        userId: user.id,
        code: error?.code || null,
        message: error?.message || String(error),
      });
      console.error('Error getting coins:', error);
      return 0;
    }

    logAuthDebug('coins_get_success', {
      userId: user.id,
      coins: data?.coins ?? null,
      hasRow: Boolean(data),
    });
    
    return data?.coins || 0;
  } catch (error) {
    logAuthDebug('coins_get_catch', {
      userId: user?.id || null,
      message: error?.message || String(error),
    });
    console.error('Unexpected error in getCoins:', error);
    return 0;
  }
};

// Verify user profile exists (created automatically by auth trigger).
// Kept as a safety-net: if the trigger somehow didn't fire (e.g. pre-trigger
// signups), a minimal INSERT is attempted. Coins and powerup defaults come
// from Postgres column defaults—no client-side override.
export const initializeProfile = async (userOverride = null) => {
  const user = userOverride || (await ensureAuthenticated()).user;
  logAuthDebug('profile_init_start', {
    hasUserOverride: Boolean(userOverride?.id),
    userId: user?.id || null,
  });
  if (!user) return false;
  
  try {
    // Check if profile exists (should already exist via auth trigger)
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();
    
    if (data) {
      logAuthDebug('profile_init_exists', { userId: user.id });
      return true;
    }
    
    if (error && error.code !== 'PGRST116') {
      logAuthDebug('profile_init_check_error', {
        userId: user.id,
        code: error?.code || null,
        message: error?.message || String(error),
      });
      console.error('Error checking profile:', error);
      return false;
    }
    
    // Safety-net: create a minimal profile row. Coins, free_skips, and
    // free_inspects are set by Postgres column defaults (250, 3, 3).
    const username = `Player_${Math.floor(Math.random() * 10000)}`;
    
    const { error: insertError } = await supabase
      .from('profiles')
      .upsert({ id: user.id, username }, { onConflict: 'id' });
      
    if (insertError) {
      logAuthDebug('profile_init_insert_error', {
        userId: user.id,
        code: insertError?.code || null,
        message: insertError?.message || String(insertError),
      });
      console.error('Error creating profile:', insertError);
      return false;
    }

    logAuthDebug('profile_init_insert_success', { userId: user.id, username });
    
    return true;
  } catch (error) {
    logAuthDebug('profile_init_catch', {
      userId: user?.id || null,
      message: error?.message || String(error),
    });
    console.error('Unexpected error in initializeProfile:', error);
    return false;
  }
};

// Add coins to user's balance
export const addCoins = async (amount) => {
  const { user } = await ensureAuthenticated();
  if (!user) return false;

  const safeAmount = Number(amount);
  if (!Number.isFinite(safeAmount) || safeAmount <= 0) return false;

  try {
    const { error: rpcError } = await supabase.rpc('add_coins', {
      p_user_id: user.id,
      p_amount: Math.floor(safeAmount),
    });
    if (rpcError) {
      console.error('add_coins RPC failed:', {
        code: rpcError?.code || null,
        message: rpcError?.message || String(rpcError),
        details: rpcError?.details || null,
        hint: rpcError?.hint || null,
      });
      return false;
    }
    return true;
  } catch (error) {
    console.error('Unexpected error in addCoins:', error);
    return false;
  }
};

// Spend coins if user has enough
export const spendCoins = async (amount) => {
  const { user } = await ensureAuthenticated();
  if (!user) return false;

  const safeAmount = Number(amount);
  if (!Number.isFinite(safeAmount) || safeAmount <= 0) return false;

  try {
    const { data: newBalance, error: rpcError } = await supabase.rpc('spend_coins', {
      p_user_id: user.id,
      p_amount: Math.floor(safeAmount),
    });

    if (rpcError) {
      console.error('spend_coins RPC failed:', {
        code: rpcError?.code || null,
        message: rpcError?.message || String(rpcError),
        details: rpcError?.details || null,
        hint: rpcError?.hint || null,
      });
      return false;
    }

    // RPC returns null when insufficient funds (see migration).
    return newBalance !== null;
  } catch (error) {
    console.error('Unexpected error in spendCoins:', error);
    return false;
  }
}; 
